<template>
  <div class="tag-heatmap">
    <el-card class="chart-card" v-loading="loading" ref="heatmapCard">
      <div slot="header" class="card-header">
        <span>标签云图</span>
        <div class="operations">
          <!-- 日期选择器 -->
          <div class="filter-item">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :clearable="true"
              :picker-options="pickerOptions"
              @change="handleDateChange">
            </el-date-picker>
          </div>
          
          <!-- 指标选择器 -->
          <div class="filter-item">
            <el-radio-group v-model="metricType" size="small" @change="handleMetricChange">
              <el-radio-button label="avgScore">平均分</el-radio-button>
              <el-radio-button label="accuracyRate">正确率</el-radio-button>
              <el-radio-button label="correctionRate">订正率</el-radio-button>
            </el-radio-group>
          </div>
          
          <div class="button-group">
            <el-tooltip content="重置筛选" placement="top">
              <el-button type="text" icon="el-icon-refresh" @click="handleReset" />
            </el-tooltip>
            <el-tooltip content="导出图片" placement="top">
              <el-button type="text" icon="el-icon-download" @click="handleExport" />
            </el-tooltip>
            <el-tooltip content="放大显示" placement="top">
              <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
            </el-tooltip>
          </div>
        </div>
      </div>
      
      <div class="chart-container" ref="heatmapChart"></div>
      
      <!-- 颜色图例 -->
      <div class="legend-container">
        <div class="legend-title">{{ metricLabel }}：</div>
        <div class="legend-items">
          <div class="legend-item">
            <span class="legend-color" style="background: #F56C6C;"></span>
            <span class="legend-text">0-20{{ metricUnit }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #E6A23C;"></span>
            <span class="legend-text">20-40{{ metricUnit }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #F7BA2A;"></span>
            <span class="legend-text">40-60{{ metricUnit }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #67C23A;"></span>
            <span class="legend-text">60-80{{ metricUnit }}</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: #5CB87A;"></span>
            <span class="legend-text">80-100{{ metricUnit }}</span>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      top="5vh"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose">
      <div slot="title" class="dialog-header">
        <span>标签云图 - {{ metricLabel }}</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'

export default {
  name: 'TagHeatmap',
  data() {
    return {
      loading: false,
      dateRange: null,
      metricType: 'avgScore',
      heatmapChart: null,
      fullscreenChart: null,
      heatmapData: [],
      dialogVisible: false,
      
      pickerOptions: {
        shortcuts: [
          {
            text: '重置',
            onClick(picker) {
              picker.$emit('pick', null)
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近半年',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一年',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  computed: {
    metricLabel() {
      const labels = {
        'avgScore': '平均分',
        'accuracyRate': '正确率',
        'correctionRate': '订正率'
      }
      return labels[this.metricType] || '平均分'
    },
    metricUnit() {
      return this.metricType === 'avgScore' ? '分' : '%'
    }
  },
  mounted() {
    this.initChart()
    this.loadData()
    
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.heatmapChart) {
      this.heatmapChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 初始化图表
    initChart() {
      if (this.heatmapChart) {
        this.heatmapChart.dispose()
      }
      
      this.heatmapChart = echarts.init(this.$refs.heatmapChart)
      this.updateChart()
    },
    
    // 加载数据
    async loadData() {
      this.loading = true
      
      try {
        const params = {}
        
        if (this.dateRange && this.dateRange.length > 0) {
          params['dateRange[]'] = this.dateRange
        }
        
        const response = await request({
          url: '/api/tag-heatmap/data',
          method: 'get',
          params
        })
        
        if (response.code === 200) {
          this.heatmapData = response.data || []
          this.updateChart()
        } else {
          this.$message.error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    // 更新图表
    updateChart() {
      if (!this.heatmapChart || !this.heatmapData.length) return
      
      const option = this.getChartOption()
      this.heatmapChart.setOption(option, true)
    },
    
    // 获取指标值
    getMetricValue(item) {
      if (this.metricType === 'avgScore') {
        return item.avgScore
      } else if (this.metricType === 'accuracyRate') {
        return item.accuracyRate
      } else if (this.metricType === 'correctionRate') {
        return item.correctionRate
      }
      return 0
    },
    
    // 根据值获取颜色（0-100的值，分为5档）
    getColor(value) {
      if (value >= 80) return '#5CB87A'      // 深绿
      if (value >= 60) return '#67C23A'      // 浅绿
      if (value >= 40) return '#F7BA2A'      // 黄色
      if (value >= 20) return '#E6A23C'      // 橙色
      return '#F56C6C'                       // 红色
    },
    
    // 处理日期变化
    handleDateChange() {
      this.loadData()
    },
    
    // 处理指标切换
    handleMetricChange() {
      this.updateChart()
    },
    
    // 窗口大小变化处理
    handleResize() {
      if (this.heatmapChart) {
        this.heatmapChart.resize()
      }
    },
    
    // 重置筛选
    handleReset() {
      this.dateRange = null
      this.metricType = 'avgScore'
      this.loadData()
    },
    
    // 导出图片
    handleExport() {
      if (!this.heatmapChart) {
        this.$message.warning('图表未加载完成')
        return
      }
      
      try {
        const imgData = this.heatmapChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        })
        
        const link = document.createElement('a')
        const fileName = `标签云图_${this.metricLabel}_${new Date().toISOString().slice(0, 10)}.png`
        link.href = imgData
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('图片下载成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    
    // 全屏显示
    handleFullscreen() {
      this.dialogVisible = true
    },
    
    // 关闭全屏
    closeDialog() {
      this.dialogVisible = false
    },
    
    // 全屏弹窗打开
    handleDialogOpen() {
      this.$nextTick(() => {
        if (this.fullscreenChart) {
          this.fullscreenChart.dispose()
        }
        
        this.fullscreenChart = echarts.init(this.$refs.fullscreenChart)
        this.updateFullscreenChart()
        
        setTimeout(() => {
          if (this.fullscreenChart) {
            this.fullscreenChart.resize()
          }
        }, 100)
      })
    },
    
    // 全屏弹窗关闭
    handleDialogClose() {
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    
    // 全屏弹窗关闭后
    handleDialogClosed() {
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    
    // 更新全屏图表
    updateFullscreenChart() {
      if (!this.fullscreenChart || !this.heatmapData.length) return
      
      const option = this.getChartOption()
      this.fullscreenChart.setOption(option, true)
    },
    
    // 导出全屏图片
    handleExportFullscreen() {
      if (!this.fullscreenChart) {
        this.$message.warning('图表未加载完成')
        return
      }
      
      try {
        const imgData = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        })
        
        const link = document.createElement('a')
        const fileName = `标签云图_全屏_${this.metricLabel}_${new Date().toISOString().slice(0, 10)}.png`
        link.href = imgData
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('图片下载成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    
    // 获取图表配置（供普通和全屏图表共用）
    getChartOption() {
      const chartData = this.heatmapData.map(item => ({
        name: item.name,
        value: item.value,
        metricValue: this.getMetricValue(item),
        itemStyle: {
          color: this.getColor(this.getMetricValue(item))
        },
        stats: item.stats
      }))
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const data = params.data
            if (!data) return ''
            
            const metricValue = data.metricValue || 0
            const stats = data.stats || { correctCount: 0, wrongCount: 0, correctedCount: 0 }
            
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${data.name || ''}</div>
                <div>题目数量: ${data.value || 0}</div>
                <div>${this.metricLabel}: ${metricValue.toFixed(1)}${this.metricUnit}</div>
                <div style="margin-top: 4px; padding-top: 4px; border-top: 1px solid #eee; font-size: 11px; color: #999;">
                  正确: ${stats.correctCount} | 错题: ${stats.wrongCount} | 已订正: ${stats.correctedCount}
                </div>
              </div>
            `
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          }
        },
        series: [
          {
            type: 'treemap',
            data: chartData,
            width: '100%',
            height: '100%',
            roam: false,
            nodeClick: false,
            breadcrumb: {
              show: false
            },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => {
                const data = params.data
                if (!data) return ''
                const metricValue = (data.metricValue || 0).toFixed(this.metricType === 'avgScore' ? 1 : 1)
                
                // 简单显示3行内容
                return `${data.name}\n${data.value}题\n${metricValue}${this.metricUnit}`
              },
              fontSize: 11,
              fontWeight: 600,
              color: '#fff',
              lineHeight: 16,
              textShadowBlur: 2,
              textShadowColor: 'rgba(0, 0, 0, 0.5)'
            },
            upperLabel: {
              show: false
            },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 0,
              gapWidth: 1
            },
            emphasis: {
              label: {
                fontSize: 14,
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0,0,0,0.3)',
                borderColor: '#333',
                borderWidth: 1
              }
            }
          }
        ]
      }
    },
    
    // 强制重新调整大小（供父组件调用）
    forceResize() {
      this.$nextTick(() => {
        if (this.heatmapChart) {
          this.heatmapChart.resize()
        }
      })
    }
  }
}
</script>

<style scoped>
.tag-heatmap {
  width: 100%;
}

.chart-card {
  margin-bottom: 16px;
}

/* 移除el-card的默认padding */
.tag-heatmap :deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

.tag-heatmap :deep(.el-card__body) {
  padding: 0;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.operations {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-item {
  display: flex;
  align-items: center;
}

.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 0;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.chart-container {
  width: 100%;
  height: 600px;
}

.legend-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-top: 1px solid #ebeef5;
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.legend-title {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  margin-right: 12px;
}

.legend-items {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-text {
  font-size: 12px;
  color: #606266;
}

/* 全屏弹窗样式 */
.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 65vh;
  padding: 20px;
  box-sizing: border-box;
}

/* 日期选择器样式 */
.filter-item :deep(.el-date-editor.el-input),
.filter-item :deep(.el-date-editor.el-range-editor) {
  width: 260px !important;
  height: 28px !important;
  line-height: 28px !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  border-radius: 4px !important;
}

.filter-item :deep(.el-date-editor.el-range-editor:hover) {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.filter-item :deep(.el-range-input) {
  background-color: transparent !important;
  color: #fff !important;
  font-size: 12px !important;
  height: 100% !important;
  margin: 0 !important;
  vertical-align: middle !important;
  width: 90px !important;
  padding: 0 2px !important;
}

.filter-item :deep(.el-range-separator) {
  color: #fff !important;
  padding: 0 3px !important;
  line-height: 22px !important;
}

.filter-item :deep(.el-range__icon),
.filter-item :deep(.el-range__close-icon),
.filter-item :deep(.el-input__icon) {
  color: #fff !important;
  line-height: 28px !important;
  font-size: 14px !important;
}

.filter-item :deep(.el-date-editor.el-range-editor) {
  padding: 0 8px !important;
  display: flex !important;
  align-items: center !important;
}

.filter-item :deep(.el-range-input::-webkit-input-placeholder) {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Radio Button 样式优化 */
.filter-item :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  background-color: #fff !important;
  border-color: #fff !important;
  box-shadow: -1px 0 0 0 #fff !important;
  color: #2E365A !important;
}

.filter-item :deep(.el-radio-button__inner) {
  background-color: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

.filter-item :deep(.el-radio-button__inner:hover) {
  background-color: rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .operations {
    flex-wrap: wrap;
  }
  
  .filter-item {
    flex: 1;
    min-width: 200px;
  }
}
</style>

<style>
/* 全局样式 - PC端弹窗位置调整 */
.chart-dialog .el-dialog {
  margin-top: 5vh !important;
}
</style>
